import React, { useState, useEffect } from 'react';
import { Card, Table, Switch, Input, Button, Space, Typography, Breadcrumb, message, Alert, Row, Col, ConfigProvider, theme } from 'antd';
import { SaveOutlined, SecurityScanOutlined, UserOutlined, LockOutlined, EyeInvisibleOutlined, EyeTwoTone } from '@ant-design/icons';
import { useQuery, useMutation } from '@tanstack/react-query';

import useAuthStore from '../../store/useAuthStore';
import { getRolesByUser, assignRoles, getAllModules } from '../../api/users.api';

import SearchableSelect from '../../components/common/SearchableSelect';

const { Text } = Typography;

/* ─────────────────────────────────────────────
   Light Theme & Compact Override Styles
───────────────────────────────────────────── */
const LightThemeStyles = () => (
  <style>{`
    .light-role-page {
      --bg: #f8fafc;
      --surface: #ffffff;
      --surface-2: #f1f5f9;
      --border: #e2e8f0;
      --border-hi: #cbd5e1;
      --text-1: #0f172a;
      --text-2: #334155;
      --text-3: #64748b;
      --accent: #f59e0b;
      font-family: 'Barlow', sans-serif;
    }

    .light-role-page .ant-card {
      background: var(--surface);
      border-color: var(--border);
      border-radius: 6px;
      box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.05);
    }
    
    .compact-section {
      background: #f8fafc;
      border: 1px solid var(--border);
      border-radius: 4px;
      padding: 12px 16px;
      margin-bottom: 16px;
    }

    .light-role-page .ant-table-thead > tr > th {
      background: var(--surface-2) !important;
      color: var(--text-2) !important;
      font-weight: 600;
      font-size: 11px;
      text-transform: uppercase;
      padding: 6px 10px !important;
      border-bottom: 1px solid var(--border-hi) !important;
    }

    .light-role-page .ant-table-tbody > tr > td {
      padding: 6px 10px !important;
      font-size: 13px;
      color: var(--text-1);
      border-bottom: 1px solid var(--border);
    }

    /* Make switches slightly smaller to fit the compact theme */
    .light-role-page .ant-switch-small {
      min-width: 28px;
      height: 16px;
      line-height: 16px;
    }
    .light-role-page .ant-switch-small .ant-switch-handle {
      width: 12px;
      height: 12px;
    }
  `}</style>
);

const UserRolePage = () => {
  const { user } = useAuthStore();
  const plantId = user?.plantID;

  const [selectedUser, setSelectedUser] = useState(null);
  const [roleData, setRoleData] = useState([]); 

  // --- Queries ---
  const { data: modules } = useQuery({
    queryKey: ['allModules'],
    queryFn: getAllModules
  });

  // --- Effect: Load User Roles when user is selected ---
  useEffect(() => {
    const fetchUserRoles = async () => {
      if (!selectedUser || !modules || modules.length === 0) return;
      
      try {
        const response = await getRolesByUser(plantId, selectedUser.loginID);
        const existingRoles = response || [];
        
        // Map response to table format
        const mergedRoles = existingRoles.map(role => ({
          moduleID: role.modules,
          moduleName: role.moduleName,
          addRecord: role.addRecord || 'N',
          editRecord: role.editRecord || 'N',
          deleteRecord: role.deleteRecord || 'N',
          editPassword: role.editPass || '',
          deletePassword: role.delPass || ''
        }));
        
        setRoleData(mergedRoles);
      } catch (err) {
        message.error("Failed to load user roles");
      }
    };

    fetchUserRoles();
  }, [selectedUser, modules, plantId]);

  // --- Mutation ---
  const saveMutation = useMutation({
    mutationFn: (payload) => assignRoles(plantId, payload),
    onSuccess: () => {
      message.success(`Permissions updated for ${selectedUser.user_Name}`);
    },
    onError: (err) => {
      message.error(err?.response?.data?.message || "Failed to save roles");
    }
  });

  // --- Handlers ---
  const handleToggle = (moduleId, field, checked) => {
    const val = checked ? 'Y' : 'N';
    setRoleData(prev => prev.map(row => 
      row.moduleID === moduleId ? { ...row, [field]: val } : row
    ));
  };

  const handlePasswordChange = (moduleId, field, value) => {
    setRoleData(prev => prev.map(row => 
      row.moduleID === moduleId ? { ...row, [field]: value } : row
    ));
  };

  const handleSave = () => {
    if (!selectedUser) return;
    
    const payload = roleData.map(role => ({
      loginID: selectedUser.loginID,
      modules: role.moduleID,
      addRecord: role.addRecord,
      editRecord: role.editRecord,
      deleteRecord: role.deleteRecord,
      editPass: role.editPassword || null,
      delPass: role.deletePassword || null
    }));

    saveMutation.mutate(payload);
  };

  // --- Table Columns ---
  const columns = [
    { 
      title: 'Module Name', 
      dataIndex: 'moduleName', 
      key: 'moduleName',
      render: (text) => <span style={{ fontWeight: 600, color: 'var(--text-1)' }}>{text}</span>
    },
    { 
      title: 'Can Add', 
      key: 'addRecord', 
      align: 'center',
      width: 90,
      render: (_, record) => (
        <Switch 
          size="small"
          checked={record.addRecord === 'Y'} 
          onChange={(checked) => handleToggle(record.moduleID, 'addRecord', checked)} 
        />
      )
    },
    { 
      title: 'Can Edit', 
      key: 'editRecord', 
      align: 'center',
      width: 90,
      render: (_, record) => (
        <Switch 
          size="small"
          checked={record.editRecord === 'Y'} 
          onChange={(checked) => handleToggle(record.moduleID, 'editRecord', checked)} 
        />
      )
    },
    { 
      title: 'Can Delete', 
      key: 'deleteRecord', 
      align: 'center',
      width: 100,
      render: (_, record) => (
        <Switch 
          size="small"
          checked={record.deleteRecord === 'Y'} 
          onChange={(checked) => handleToggle(record.moduleID, 'deleteRecord', checked)} 
        />
      )
    },
    { 
      title: 'Edit Password (Optional)', 
      key: 'editPassword',
      width: 200,
      render: (_, record) => (
        <Input.Password
          placeholder="Set Pin"
          size="small"
          iconRender={visible => (visible ? <EyeTwoTone /> : <EyeInvisibleOutlined />)}
          value={record.editPassword}
          onChange={(e) => handlePasswordChange(record.moduleID, 'editPassword', e.target.value)}
          prefix={<LockOutlined style={{ fontSize: 12, color: '#94a3b8' }} />}
          disabled={record.editRecord === 'N'}
          style={{ borderRadius: 4 }}
        />
      )
    },
    { 
      title: 'Delete Password (Optional)', 
      key: 'deletePassword',
      width: 200,
      render: (_, record) => (
        <Input.Password
          placeholder="Set Pin"
          size="small"
          iconRender={visible => (visible ? <EyeTwoTone /> : <EyeInvisibleOutlined />)}
          value={record.deletePassword}
          onChange={(e) => handlePasswordChange(record.moduleID, 'deletePassword', e.target.value)}
          prefix={<LockOutlined style={{ fontSize: 12, color: '#94a3b8' }} />}
          disabled={record.deleteRecord === 'N'}
          style={{ borderRadius: 4 }}
        />
      )
    }
  ];

  return (
    <ConfigProvider theme={{ algorithm: theme.defaultAlgorithm }}>
      <LightThemeStyles />
      <div className="light-role-page" style={{ padding: '8px 16px' }}>
        
        {/* BREADCRUMB */}
        <Breadcrumb 
          style={{ marginBottom: 12, fontSize: 12, fontWeight: 500 }}
          items={[
            { title: 'Home' },
            { title: 'Admin' },
            { title: 'User Roles' },
            ...(selectedUser ? [{ title: <span style={{ color: 'var(--text-1)' }}>{selectedUser.user_Name}</span> }] : [])
          ]}
        />

        <Card 
          className="report-card"
          styles={{ 
            header: { padding: '10px 16px', minHeight: 40, background: '#f8fafc', borderBottom: '1px solid #e2e8f0' }, 
            body: { padding: '16px' } 
          }}
          title={
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <SecurityScanOutlined style={{ color: 'var(--text-3)' }} />
              <Space direction="vertical" size={0}>
                <span style={{ fontSize: 16, fontWeight: 700, textTransform: 'uppercase', color: 'var(--text-1)', lineHeight: 1 }}>
                  Role & Permission Assignment
                </span>
                <span style={{ fontSize: 11, color: 'var(--text-3)', fontWeight: 400 }}>
                  Manage individual module access and secure actions with PINs
                </span>
              </Space>
            </div>
          }
        >
          {/* STEP 1: SELECT USER */}
          <div className="compact-section" style={{ display: 'flex', gap: 16, alignItems: 'center', flexWrap: 'wrap' }}>
            <div style={{ flex: 1, minWidth: 260 }}>
              <span style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-2)', display: 'block', marginBottom: 6, textTransform: 'uppercase' }}>
                1. Select User to Configure
              </span>
              <SearchableSelect
                size="small"
                apiUrl={`/plants/${plantId}/users`}
                valueField="loginID"
                labelField="loginID"
                placeholder="Search user by login ID..."
                onSelect={(u) => setSelectedUser(u)}
                prefix={<UserOutlined style={{ color: 'var(--text-3)' }}/>}
              />
            </div>
            
            {selectedUser && (
              <div style={{ flex: 2, minWidth: 300, display: 'flex', alignItems: 'flex-end' }}>
                <Alert 
                  message={
                    <span style={{ fontSize: 13, color: '#0369a1' }}>
                      Configuring permissions for: <b>{selectedUser.user_Name}</b> ({selectedUser.loginID})
                    </span>
                  }
                  type="info" 
                  showIcon 
                  style={{ padding: '6px 12px', background: '#f0f9ff', border: '1px solid #bae6fd', width: '100%' }}
                />
              </div>
            )}
          </div>

          {/* STEP 2: PERMISSION TABLE */}
          {selectedUser ? (
            <div style={{ animation: 'fadeIn 0.3s ease-in-out' }}>
              <span style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-2)', display: 'block', marginBottom: 8, textTransform: 'uppercase' }}>
                2. Set Permissions
              </span>
              <div style={{ border: '1px solid var(--border)', borderRadius: '6px', overflow: 'hidden' }}>
                <Table 
                  columns={columns} 
                  dataSource={roleData} 
                  rowKey="moduleID" 
                  pagination={false} 
                  size="small"
                  scroll={{ x: 800 }}
                />
              </div>
              
              <div style={{ marginTop: 16, display: 'flex', justifyContent: 'flex-end', background: '#f8fafc', padding: '12px 16px', borderRadius: 4, border: '1px solid #e2e8f0' }}>
                <Button 
                  type="primary" 
                  icon={<SaveOutlined />} 
                  loading={saveMutation.isPending}
                  onClick={handleSave}
                  style={{ background: '#0f172a', fontWeight: 600, padding: '0 24px' }}
                >
                  Save All Permissions
                </Button>
              </div>
            </div>
          ) : (
            <div style={{ padding: '50px 0', textAlign: 'center', background: '#f8fafc', borderRadius: 6, border: '1px dashed #cbd5e1' }}>
              <SecurityScanOutlined style={{ fontSize: 28, color: '#cbd5e1', marginBottom: 8 }} />
              <div style={{ color: 'var(--text-3)', fontSize: 13 }}>Please select a user from the dropdown above to manage their permissions.</div>
            </div>
          )}
        </Card>
      </div>
    </ConfigProvider>
  );
};

export default UserRolePage;